// Aba LOGS (Editar Usuário) — histórico real de atividade do usuário:
// login, erros de ações que falharam, comandos de veículo (bloquear/
// desbloquear), geocerca e notificações push (quando/qual aparelho ativou
// + alertas disparados para a empresa dele). Ver
// gpsdrive-activity-log.service.ts e useUserActivityLog.
import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertTriangle,
  BellRing,
  History,
  Lock,
  LogIn,
  MapPinned,
  Smartphone,
  Terminal,
  Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { describeUserAgent } from "@/lib/user-agent";
import { useUserActivityLog } from "@/hooks/queries";
import type { ActivityLogEntry, ActivityLogType } from "@/services/gpsdrive-activity-log.service";

const PAGE_SIZE = 30;

type FilterGroup = "all" | "login" | "errors" | "vehicles" | "geofences" | "notifications";

const FILTERS: { key: FilterGroup; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "login", label: "Login" },
  { key: "errors", label: "Erros" },
  { key: "vehicles", label: "Veículos" },
  { key: "geofences", label: "Geocercas" },
  { key: "notifications", label: "Notificações" },
];

// A filtragem por grupo é feita no cliente (não no backend, que só sabe
// filtrar por um `type` exato de cada vez) — mais simples do que ensinar a
// API a entender "grupo", e o volume esperado é baixo.
function groupOf(type: ActivityLogType): FilterGroup {
  switch (type) {
    case "LOGIN":
      return "login";
    case "ERROR":
      return "errors";
    case "VEHICLE_BLOCK":
    case "VEHICLE_UNBLOCK":
    case "VEHICLE_COMMAND":
    case "HISTORY_DELETED":
    case "HISTORY_RESTORED":
      return "vehicles";
    case "GEOFENCE_CREATED":
    case "GEOFENCE_UPDATED":
    case "GEOFENCE_DELETED":
      return "geofences";
    case "PUSH_SUBSCRIBED":
    case "NOTIFICATION_RECEIVED":
      return "notifications";
  }
}

const TYPE_META: Record<ActivityLogType, { label: string; icon: typeof LogIn; tone: string }> = {
  LOGIN: { label: "Login", icon: LogIn, tone: "text-emerald-600" },
  ERROR: { label: "Erro", icon: AlertTriangle, tone: "text-red-600" },
  VEHICLE_BLOCK: { label: "Veículo bloqueado", icon: Lock, tone: "text-amber-600" },
  VEHICLE_UNBLOCK: { label: "Veículo desbloqueado", icon: Unlock, tone: "text-amber-600" },
  VEHICLE_COMMAND: { label: "Comando de veículo", icon: Terminal, tone: "text-slate-600" },
  GEOFENCE_CREATED: { label: "Geocerca criada", icon: MapPinned, tone: "text-blue-600" },
  GEOFENCE_UPDATED: { label: "Geocerca editada", icon: MapPinned, tone: "text-blue-600" },
  GEOFENCE_DELETED: { label: "Geocerca excluída", icon: MapPinned, tone: "text-blue-600" },
  HISTORY_DELETED: { label: "Histórico apagado", icon: History, tone: "text-rose-600" },
  HISTORY_RESTORED: { label: "Histórico recuperado", icon: History, tone: "text-emerald-600" },
  PUSH_SUBSCRIBED: { label: "Notificação push ativada", icon: Smartphone, tone: "text-violet-600" },
  NOTIFICATION_RECEIVED: { label: "Notificação", icon: BellRing, tone: "text-violet-600" },
};

export function UserActivityLogTab({ userId }: { userId: string | undefined }) {
  const [filter, setFilter] = useState<FilterGroup>("all");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<ActivityLogEntry[]>([]);
  const { data, isLoading, isFetching } = useUserActivityLog(userId, { limit: PAGE_SIZE, offset }, !!userId);

  // Reseta a lista acumulada ao trocar de usuário (dialog reaberto para
  // outra pessoa) — nunca deve misturar log de dois usuários diferentes.
  useEffect(() => {
    setItems([]);
    setOffset(0);
  }, [userId]);

  useEffect(() => {
    if (!data) return;
    setItems((prev) => (offset === 0 ? data.items : [...prev, ...data.items]));
  }, [data, offset]);

  const total = data?.total ?? items.length;
  const visible = filter === "all" ? items : items.filter((entry) => groupOf(entry.type) === filter);
  const hasMore = items.length < total;

  return (
    <div>
      <div className="mb-2 text-sm font-medium">Histórico</div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              filter === f.key
                ? "border-slate-800 bg-slate-800 text-white"
                : "border-border bg-background text-muted-foreground hover:bg-muted",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && items.length === 0 ? (
        <div className="py-16 text-center text-xs text-muted-foreground">Carregando…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-16 text-center text-xs text-muted-foreground">
          Sem dados.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((entry) => {
            const meta = TYPE_META[entry.type];
            const Icon = meta?.icon ?? Terminal;
            const deviceLabel =
              entry.type === "PUSH_SUBSCRIBED" ? describeUserAgent(entry.metadata?.userAgent) : null;
            const hasMetadata = entry.metadata && Object.keys(entry.metadata).length > 0;
            return (
              <div key={entry.id} className="rounded-lg border border-border bg-background p-3 text-xs">
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted",
                      meta?.tone,
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="font-semibold text-foreground">{meta?.label ?? entry.type}</span>
                      {!entry.success && (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                          falhou
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true, locale: ptBR })}
                      </span>
                    </div>
                    <div className="mt-0.5 text-muted-foreground">{entry.message}</div>
                    {deviceLabel && <div className="mt-0.5 text-muted-foreground">{deviceLabel}</div>}
                    {hasMetadata && (
                      <details className="mt-1">
                        <summary className="cursor-pointer select-none text-muted-foreground/80">Detalhes</summary>
                        <pre className="mt-1 overflow-x-auto rounded bg-muted/50 p-2 text-[10px]">
                          {JSON.stringify(entry.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            disabled={isFetching}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            {isFetching ? "Carregando…" : "Carregar mais"}
          </Button>
        </div>
      )}
    </div>
  );
}
